import { Calculator, Percent } from 'lucide-react';
import type { PillField } from '@/components/common/LabeledPill';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { divideToHundredths, formatPercentOf, formatTwoDecimals } from '@/utils/number';
import type { SystemDashboardTotals } from '../types/dashboard.types';

type TotalKey = keyof SystemDashboardTotals;

interface TotalMetric {
  key: TotalKey;
  label: string;
  isTaka: boolean;
  approvedShareOf?: TotalKey;
}

interface SystemDashboardSummary {
  totals: SystemDashboardTotals | undefined;
  zmCount: number | undefined;
}

const TOTAL_METRICS: readonly TotalMetric[] = [
  { key: 'dcrEntered', label: 'DCR Entered', isTaka: false },
  { key: 'dcrApproved', label: 'DCR Approved', isTaka: false, approvedShareOf: 'dcrEntered' },
  { key: 'derEntered', label: 'DER Entered', isTaka: false },
  { key: 'derApproved', label: 'DER Approved', isTaka: false, approvedShareOf: 'derEntered' },
  { key: 'dcrClaimedAmount', label: 'DCR Taka Claimed', isTaka: true },
  {
    key: 'dcrApprovedAmount',
    label: 'DCR Taka Approved',
    isTaka: true,
    approvedShareOf: 'dcrClaimedAmount',
  },
  { key: 'derClaimedAmount', label: 'DER Taka Claimed', isTaka: true },
  {
    key: 'derApprovedAmount',
    label: 'DER Taka Approved',
    isTaka: true,
    approvedShareOf: 'derClaimedAmount',
  },
  { key: 'deliveredQty', label: 'SD Delivered', isTaka: false },
];

const SUMMARY_PILL_KEYS = {
  dcrApproved: 'dcrApprovedShare',
  derApproved: 'derApprovedShare',
  takaApproved: 'takaApprovedShare',
  averageDcr: 'averageDcrPerZm',
} as const;

const HINT_SEPARATOR = ' · ';

const toAverage = (total: number, zmCount: number): string =>
  formatTwoDecimals(divideToHundredths(total, zmCount));

function getCardHint(metric: TotalMetric, summary: SystemDashboardSummary): string | undefined {
  const { totals, zmCount } = summary;
  if (!totals || !zmCount) return undefined;

  const average = toAverage(totals[metric.key], zmCount);
  const averageText = `Average per ZM: ${metric.isTaka ? `${TAKA_SYMBOL} ${average}` : average}`;
  if (!metric.approvedShareOf) return averageText;

  const approvedShare = formatPercentOf(totals[metric.key], totals[metric.approvedShareOf]);
  return `${averageText}${HINT_SEPARATOR}Approved: ${approvedShare}`;
}

export function getSystemDashboardStatCards(summary: SystemDashboardSummary): LabeledStat[] {
  return TOTAL_METRICS.map((metric) => ({
    key: metric.key,
    label: metric.label,
    value: summary.totals?.[metric.key],
    unit: metric.isTaka ? TAKA_SYMBOL : undefined,
    hint: getCardHint(metric, summary),
  }));
}

function getLoadingPills(): PillField[] {
  return Object.values(SUMMARY_PILL_KEYS).map((key) => ({
    key,
    icon: Percent,
    label: '',
    value: undefined,
    isLoading: true,
  }));
}

export function getSystemDashboardSummaryPills({
  totals,
  zmCount,
}: SystemDashboardSummary): PillField[] {
  if (!totals || zmCount === undefined) return getLoadingPills();
  if (zmCount === 0) return [];

  const takaClaimed = totals.dcrClaimedAmount + totals.derClaimedAmount;
  const takaApproved = totals.dcrApprovedAmount + totals.derApprovedAmount;
  const pills: PillField[] = [
    {
      key: SUMMARY_PILL_KEYS.dcrApproved,
      icon: Percent,
      label: 'DCR Approved',
      value: formatPercentOf(totals.dcrApproved, totals.dcrEntered),
      isLoading: false,
    },
    {
      key: SUMMARY_PILL_KEYS.derApproved,
      icon: Percent,
      label: 'DER Approved',
      value: formatPercentOf(totals.derApproved, totals.derEntered),
      isLoading: false,
    },
  ];
  if (takaClaimed > 0) {
    pills.push({
      key: SUMMARY_PILL_KEYS.takaApproved,
      icon: Percent,
      label: 'Taka Approved',
      value: formatPercentOf(takaApproved, takaClaimed),
      isLoading: false,
    });
  }
  pills.push({
    key: SUMMARY_PILL_KEYS.averageDcr,
    icon: Calculator,
    label: 'Average DCR per ZM',
    value: toAverage(totals.dcrEntered, zmCount),
    isLoading: false,
  });
  return pills;
}
